import { EVENT_VISIBLE_ELEMENT } from '../settings';
import { Detector, OnDetectionCallback } from '../types';

class VisibleElementDetector<T> implements Detector<T> {
  elements: Map<Element, boolean>;

  onDetection: OnDetectionCallback<T>;

  visibleTimestamp: Map<Element, number>;

  observer: IntersectionObserver;

  constructor(
    targets: Element[],
    onDetection?: OnDetectionCallback<T>,
    intersectionObserverOptions = {},
  ) {
    this.elements = new Map();
    this.onDetection = (...args) => {
      onDetection?.apply(this, args);
    };
    this.visibleTimestamp = new Map();
    Array.from(targets).forEach((element) => {
      this.elements.set(element, false);
    });
    this.observer = new IntersectionObserver(this.update.bind(this), intersectionObserverOptions);
  }

  static isVisible(elem: Element) {
    // throw Error('DomUtil: elem is not an element.');
    if (!(elem instanceof HTMLElement)) return false;
    const style = getComputedStyle(elem);
    if (style.display === 'none') return false;
    if (style.visibility !== 'visible') return false;
    if (parseFloat(style.opacity) < 0.1) return false;
    if (
      elem.offsetWidth + elem.offsetHeight
      + elem.getBoundingClientRect().height + elem.getBoundingClientRect().width === 0
    ) {
      return false;
    }
    const elemCenter = {
      x: elem.getBoundingClientRect().left + elem.offsetWidth / 2,
      y: elem.getBoundingClientRect().top + elem.offsetHeight / 2,
    };
    if (elemCenter.x < 0) return false;
    if (elemCenter.x > (document.documentElement.clientWidth || window.innerWidth)) return false;
    if (elemCenter.y < 0) return false;
    if (elemCenter.y > (document.documentElement.clientHeight || window.innerHeight)) return false;
    let pointContainer = document.elementFromPoint(elemCenter.x, elemCenter.y);
    if (!pointContainer) return false;
    do {
      if (pointContainer === elem) return true;
    // eslint-disable-next-line no-cond-assign
    } while (pointContainer = pointContainer.parentNode as Element | null);
    return false;
  }

  start() {
    this.elements.forEach((_visible, element) => {
      this.observer.observe(element);
      if (VisibleElementDetector.isVisible(element)) {
        this.elements.set(element, true);
        this.visibleTimestamp.set(element, Date.now());
      }
    });
    return this;
  }

  stop() {
    const visibleElementsStopped: Array<{
      startTime: number;
      duration: number;
      element: Element;
    }> = [];
    this.elements.forEach((_visible, element) => {
      this.observer.unobserve(element);
      // TODO: trigger from older duration to most recent
      const startTime = this.visibleTimestamp.get(element);
      if (startTime) {
        const duration = Date.now() - startTime;
        visibleElementsStopped.push({
          startTime,
          duration,
          element,
        });
      }
      this.visibleTimestamp.delete(element);
    });
    visibleElementsStopped.sort((vElem1, vElem2) => (
      vElem1.duration - vElem2.duration
    )).forEach(({ startTime, duration, element }) => {
      this.onDetection(
        element,
        new Event(EVENT_VISIBLE_ELEMENT),
        startTime,
        duration,
      );
    });
    return this;
  }

  update(entries: IntersectionObserverEntry[]) {
    entries.forEach((entry) => {
      const { target } = entry;
      const isVisible = entry.isIntersecting;
      const hasVisibilityChanged = isVisible !== this.isVisible(target);
      this.elements.set(target, isVisible);
      if (hasVisibilityChanged) {
        this.handleVisibilityChange(target, entry);
      }
    });
  }

  isVisible(element: Element) {
    return this.elements.get(element) ?? false;
  }

  handleVisibilityChange(element: Element, entry: IntersectionObserverEntry) {
    if (this.isVisible(element)) {
      this.visibleTimestamp.set(element, Date.now());
    } else {
      const visibleTimestamp = this.visibleTimestamp.get(element);
      const currentTime = Date.now();
      const duration = visibleTimestamp ? currentTime - visibleTimestamp : entry.time;
      const startTime = currentTime - entry.time;
      this.onDetection(
        element,
        new Event(EVENT_VISIBLE_ELEMENT),
        startTime,
        duration,
        { ...(entry as unknown as T) },
      );
      this.visibleTimestamp.delete(element);
    }
  }
}

export default VisibleElementDetector;
